import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import Icon from './Icon';

interface CountdownTimerProps {
  endTime: Date | string;
  onEnd?: () => void;
  showWarning?: boolean;
  className?: string;
}

export default function CountdownTimer({ 
  endTime, 
  onEnd,
  showWarning = true,
  className 
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });

  const calculateTimeLeft = useCallback(() => {
    const end = typeof endTime === 'string' ? new Date(endTime) : endTime;
    const now = new Date();
    const difference = end.getTime() - now.getTime();

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      total: difference,
    };
  }, [endTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.total <= 0) {
        clearInterval(timer);
        onEnd?.();
      }
    }, 1000);

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [calculateTimeLeft, onEnd]);

  const isEnding = timeLeft.total > 0 && timeLeft.total < 5 * 60 * 1000; // Less than 5 minutes
  const isCritical = timeLeft.total > 0 && timeLeft.total < 60 * 1000; // Less than 1 minute
  const hasEnded = timeLeft.total <= 0;

  if (hasEnded) {
    return (
      <div className={clsx('text-center', className)}>
        <span className="text-xl font-bold text-gray-500">Auction Ended</span>
      </div>
    );
  }

  const formatUnit = (value: number, unit: string) => (
    <div className="flex flex-col items-center">
      <span className={clsx(
        'text-2xl font-bold tabular-nums',
        isCritical && 'text-red-600 animate-pulse',
        isEnding && !isCritical && 'text-orange-600',
        !isEnding && 'text-gray-900'
      )}>
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-xs text-gray-500 uppercase">{unit}</span>
    </div>
  );

  return (
    <div className={clsx('flex items-center justify-center gap-1', className)}>
      {showWarning && isEnding && (
        <Icon name="alarm" className="mr-2 text-lg animate-bounce text-orange-500" />
      )}
      
      {timeLeft.days > 0 && (
        <>
          {formatUnit(timeLeft.days, 'days')}
          <span className="text-2xl font-bold text-gray-300 mx-1">:</span>
        </>
      )}
      
      {formatUnit(timeLeft.hours, 'hrs')}
      <span className="text-2xl font-bold text-gray-300 mx-1">:</span>
      {formatUnit(timeLeft.minutes, 'min')}
      <span className="text-2xl font-bold text-gray-300 mx-1">:</span>
      {formatUnit(timeLeft.seconds, 'sec')}

      {showWarning && isEnding && (
        <span className={clsx(
          'ml-3 px-2 py-1 rounded-full text-xs font-semibold',
          isCritical 
            ? 'bg-red-100 text-red-700 animate-pulse' 
            : 'bg-orange-100 text-orange-700'
        )}>
          {isCritical ? 'ENDING NOW!' : 'ENDING SOON'}
        </span>
      )}
    </div>
  );
}
